// src/services/widgetService.ts
import api from '../../utils/axios';
import { startOfDay, endOfDay, subDays } from 'date-fns';
import { ESTADOS_VENTA } from '../../constants/estados';

interface WidgetMetrics {
    cantidad: number;
    diferencia: number;
}

interface VentasMetricsResult {
    ventasHoy: number;
    ventasAyer: number;
    pendientesHoy: number;
    pendientesAyer: number;
}

export class WidgetServiceApi {
    private static cachedVentas: any = null;
    private static lastFetch: Date | null = null;

    private static async fetchVentas() {
        // Si ya tenemos datos cacheados en los últimos 5 minutos, los reutilizamos
        const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos en milisegundos
        const now = new Date();

        if (this.cachedVentas && this.lastFetch &&
            (now.getTime() - this.lastFetch.getTime()) < CACHE_DURATION) {
            return this.cachedVentas;
        }

        try {
            const ventasResponse = await api.post('/ventas/obtener-todas');
            this.cachedVentas = ventasResponse.data;
            this.lastFetch = now;
            return this.cachedVentas;
        } catch (error) {
            console.error('Error fetching ventas:', error);
            throw error;
        }
    }

    private static async getDailyMetrics(): Promise<VentasMetricsResult> {
        const ventas = await this.fetchVentas();

        const today = new Date();
        // Usar fecha local, no UTC
        const todayStr = today.getFullYear() + '-' +
            String(today.getMonth() + 1).padStart(2, '0') + '-' +
            String(today.getDate()).padStart(2, '0');

        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayStr = yesterday.getFullYear() + '-' +
            String(yesterday.getMonth() + 1).padStart(2, '0') + '-' +
            String(yesterday.getDate()).padStart(2, '0');

        let ventasHoy = 0;
        let ventasAyer = 0;
        let pendientesHoy = 0;
        let pendientesAyer = 0;

        ventas.forEach((venta: any) => {
            // Extraer solo la fecha YYYY-MM-DD
            const ventaDateStr = venta.informacionVenta.fechaVenta.substring(0, 10);
            const estadoVenta = venta.informacionVenta.estadoVenta;

            if (ventaDateStr === todayStr) {
                if (estadoVenta === 4) ventasHoy++;
                if (estadoVenta === 2) pendientesHoy++;
            } else if (ventaDateStr === yesterdayStr) {
                if (estadoVenta === 4) ventasAyer++;
                if (estadoVenta === 2) pendientesAyer++;
            }
        });

       

        return { ventasHoy, ventasAyer, pendientesHoy, pendientesAyer };
    }

    static async getFacturasPendientesMetrics(): Promise<WidgetMetrics> {
        try {
            const { pendientesHoy, pendientesAyer } = await this.getDailyMetrics();

            // Calcular diferencia porcentual respecto al día anterior
            const diferencia = pendientesAyer === 0
                ? (pendientesHoy > 0 ? 100 : 0)  // Si no había pendientes ayer pero hay hoy
                : ((pendientesHoy - pendientesAyer) / pendientesAyer) * 100;

            return {
                cantidad: pendientesHoy,  // Mostrar cantidad de pendientes de hoy
                diferencia: Math.round(diferencia)
            };
        } catch (error) {
            console.error('Error getting facturas pendientes metrics:', error);
            return { cantidad: 0, diferencia: 0 };
        }
    }

    static async getVentasMetrics(): Promise<WidgetMetrics> {
        try {
            const { ventasHoy, ventasAyer } = await this.getDailyMetrics();

            const diferencia = ventasAyer === 0 ? 0 :
                ((ventasHoy - ventasAyer) / ventasAyer) * 100;

            return {
                cantidad: ventasHoy,
                diferencia: Math.round(diferencia)
            };
        } catch (error) {
            console.error('Error getting ventas metrics:', error);
            return { cantidad: 0, diferencia: 0 };
        }
    }

    /*  static async getFacturasPendientesMetrics(): Promise<WidgetMetrics> {
         try {
             const { pendientesHoy, pendientesAyer } = await this.getDailyMetrics();
 
             const diferencia = pendientesAyer === 0 ? 0 :
                 ((pendientesHoy - pendientesAyer) / pendientesAyer) * 100;
 
             return {
                 cantidad: pendientesHoy,
                 diferencia: Math.round(diferencia)
             };
         } catch (error) {
             console.error('Error getting facturas pendientes metrics:', error);
             return { cantidad: 0, diferencia: 0 };
         }
     } */

    static async getDevolucionesMetrics(): Promise<WidgetMetrics> {
        try {
            const today = new Date();

            // Fechas para el mes actual
            const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
            const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

            // Fechas para el mes anterior
            const firstDayLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
            const lastDayLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);

            // Obtener devoluciones del mes actual
            const devolucionesMesActual = await api.post(
                '/devoluciones-venta/contar',
                {
                    fechaInicio: firstDayOfMonth.toISOString(),
                    fechaFin: lastDayOfMonth.toISOString()
                }
            );

            // Obtener devoluciones del mes anterior
            const devolucionesMesAnterior = await api.post(
                '/devoluciones-venta/contar',
                {
                    fechaInicio: firstDayLastMonth.toISOString(),
                    fechaFin: lastDayLastMonth.toISOString()
                }
            );

            // Calcular diferencia porcentual
            const cantidadActual = devolucionesMesActual.data.total;
            const cantidadAnterior = devolucionesMesAnterior.data.total;

            const diferencia = cantidadAnterior === 0 ? 0 :
                ((cantidadActual - cantidadAnterior) / cantidadAnterior) * 100;

            return {
                cantidad: cantidadActual,
                diferencia: Math.round(diferencia)
            };
        } catch (error) {
            console.error('Error getting devoluciones metrics:', error);
            return { cantidad: 0, diferencia: 0 };
        }
    }

    static async getBalanceMetrics(): Promise<WidgetMetrics> {
        try {
            // Obtener todas las cotizaciones
            const cotizacionesResponse = await api.post('/cotizaciones/obtener-todas');
            const cotizaciones = cotizacionesResponse.data;

            const today = new Date();
            // Fechas para el mes actual (Enero)
            const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
            const lastDayOfMonth = endOfDay(today);

            // Fechas para el mes anterior (Diciembre)
            const firstDayLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
            const lastDayLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);

            // Contar cotizaciones del mes actual
            const cotizacionesMesActual = cotizaciones.filter((cot: any) => {
                const fechaCotizacion = new Date(cot.informacionCotizacion.fechaCotizacion);
                return fechaCotizacion >= firstDayOfMonth &&
                    fechaCotizacion <= lastDayOfMonth &&
                    cot.informacionCotizacion.estadoCotizacion === 2; // Estado finalizada
            }).length;

            // Contar cotizaciones del mes anterior
            const cotizacionesMesAnterior = cotizaciones.filter((cot: any) => {
                const fechaCotizacion = new Date(cot.informacionCotizacion.fechaCotizacion);
                return fechaCotizacion >= firstDayLastMonth &&
                    fechaCotizacion <= lastDayLastMonth &&
                    cot.informacionCotizacion.estadoCotizacion === 2; // Estado finalizada
            }).length;

            // Calcular diferencia porcentual
            const diferencia = cotizacionesMesAnterior === 0 ? 0 :
                ((cotizacionesMesActual - cotizacionesMesAnterior) / cotizacionesMesAnterior) * 100;

           

            return {
                cantidad: cotizacionesMesActual,
                diferencia: Math.round(diferencia)
            };
        } catch (error) {
            console.error('Error getting cotizaciones metrics:', error);
            return { cantidad: 0, diferencia: 0 };
        }
    }
}